import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { ClientError, GraphQLClient, RequestDocument } from 'graphql-request'
import { dustinAuth } from '@dustin-web/auth-js'
import { MicrositeSettings } from '@dustin-web/microsite-config'
import { createPersistedQueryFetch } from '../graphql-request/persisted-query'

export type GraphqlContext = {
  cacheKey?: string
  headers?: Record<string, string>
  keepPreviousResult?: boolean
  skip?: boolean
  verbose?: boolean
}

export const DEFAULT_GRAPHQL_CONTEXT: GraphqlContext = {
  keepPreviousResult: true,
}

const isServer = typeof window === 'undefined'

export type GraphqlFetcherWithCacheKey = ReturnType<typeof getGraphqlFetcherWithCacheKey>

const getOperationName = (document: RequestDocument) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const valueObject = document.valueOf() as string | (object & { definitions: any[] })
  return typeof valueObject === 'string'
    ? valueObject
    : valueObject?.definitions?.find(d => d.kind === 'OperationDefinition')?.name?.value
}

export const getCacheKeyFromDocument = <Variables>(
  document: RequestDocument,
  variables?: Variables
) => {
  const operationName = getOperationName(document) || 'UKNOWN_OPERATION'
  const key = variables ? ':' + JSON.stringify(variables) : ''

  return `${operationName}${key}`.replace(/"([^"]+)":/g, '$1:')
}

export type GraphqlFetcher = <TQuery = unknown, TVariables = Record<string, unknown>>(
  document: RequestDocument | TypedDocumentNode<TQuery, TVariables>,
  variables?: TVariables,
  onError?: (error: ClientError) => void
) => Promise<TQuery>

export type FetcherWithCacheKeyResult<TQuery> = Promise<[string, TQuery]>
export const getGraphqlFetcherWithCacheKey = (
  micrositeSettings: MicrositeSettings,
  cookieHeader?: string
) => {
  const fetcherWithCacheKey = async <TQuery = unknown, TVariables = Record<string, unknown>>(
    document: RequestDocument | TypedDocumentNode<TQuery, TVariables>,
    {
      variables,
      context,
    }: {
      variables?: TVariables
      context?: GraphqlContext
    } = {}
  ): FetcherWithCacheKeyResult<TQuery> => {
    if (context?.skip) {
      return ['', {} as TQuery]
    }
    const contextWithDefaults = { ...DEFAULT_GRAPHQL_CONTEXT, ...context }

    const fetcher = dangerously_getGraphqlFetcher(
      micrositeSettings,
      cookieHeader,
      contextWithDefaults
    )
    const cacheKey = context?.cacheKey || getCacheKeyFromDocument(document, variables)

    const request = fetcher.fetchGraphql<TQuery, TVariables>(document, variables)

    return Promise.all([cacheKey, request])
  }

  return fetcherWithCacheKey
}

export const dangerously_getGraphqlFetcher = (
  micrositeSettings: MicrositeSettings,
  cookieHeader = '',
  context: GraphqlContext,
  usePersistedQueries = false
) => {
  const authClient = dustinAuth(
    isServer ? micrositeSettings.authServerConfig : micrositeSettings.authConfig,
    cookieHeader
  )

  const { dustinHeaders, graphqlServerUri, graphqlUri } = micrositeSettings.graphqlConfig
  const dustinGraphqlUri = isServer ? graphqlServerUri : graphqlUri

  const headers = isServer ? { ...dustinHeaders, cookie: cookieHeader } : dustinHeaders

  const client = new GraphQLClient(dustinGraphqlUri, {
    headers,
    // Every operation goes over POST. GraphQL over GET makes a query a
    // CORS-simple request, so any site can call the gateway cross-origin
    // without a preflight. The gateway rejects GET from production, see
    // TH-2246.
    method: 'POST',
    credentials: 'same-origin',
    fetch: isServer || !usePersistedQueries ? fetch : createPersistedQueryFetch(fetch),
  })

  const fetchGraphql: GraphqlFetcher = async (document, variables, onError) => {
    const token = await authClient.getAccessToken()

    if (token) {
      client.setHeader('authorization', `Bearer ${token}`)
    }

    client.setEndpoint(dustinGraphqlUri)

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const result = await client.request(document, variables)

      const verbose = context.verbose === false ? false : true

      if (isServer && process.env.NODE_ENV !== 'production' && verbose) {
        import('@dustin-web/microsite-config/src/logger').then(({ log }) => {
          const operationName = getOperationName(document)
          log.info({ variables }, `Network request: ${operationName}`)
        })
      }

      return result
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (isServer) {
        import('@dustin-web/microsite-config/src/logger').then(({ log }) => {
          const operationName = getOperationName(document)
          const debug = process.env.NODE_ENV !== 'production' ? { variables, err } : {}
          log.warn({ operationName, ...debug }, `GraphQL ssr error: ${operationName}`)
        })

        throw new Error(JSON.stringify(err))
      } else {
        console.error('Graphql browser error', { err })

        if (onError) {
          onError(err as ClientError)
        } else if (
          err?.statusCode === 401 ||
          err?.response?.status === 401 ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          err?.response?.errors?.some((err: any) => err.extensions?.code === 'UNAUTHORIZED') ||
          err?.response?.errors?.some((err: any) => err.extensions?.code === 'AUTH_NOT_AUTHORIZED')
        ) {
          window.location.href = `/login?returnUrl=${window.location.href}`
        }
      }

      return err?.response?.data
    }
  }

  // return { fetchGraphql, batchFetchGraphql }
  return { fetchGraphql }
}
